import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AmbientBackground from '../common/AmbientBackground';
import Button from '../common/Button';

const TarotCenter: React.FC = () => {
  const navigate = useNavigate();

  const handleStartReading = () => {
    navigate('/new-reading');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <AmbientBackground mood="sun" className="min-h-[calc(100vh-8rem)]" backgroundImage="/bg-center.png">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-4xl mx-auto px-4 py-12 space-y-10"
      >
        {/* Greeting */}
        <h1 className="font-display italic text-4xl md:text-5xl text-ink dark:text-parchment font-light" style={{ textShadow: '0 1px 10px rgba(245,240,232,0.7)' }}>
          Welcome back.
        </h1>

        {/* Start a Reading — full width hero */}
        <div className="bg-vellum dark:bg-shadow/80 border border-mist dark:border-dusk rounded-xl py-12 px-8">
          <h2 className="font-display text-3xl font-semibold text-sun dark:text-ember mb-4">
            Start a Reading
          </h2>
          <p className="text-ink dark:text-parchment/60 font-sans max-w-2xl mb-8">
            Choose from multiple spread layouts, from simple one-card pulls to the
            detailed Celtic Cross. Your reading will be saved to your journal,
            complete with enhanced interpretations you can revisit anytime.
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={handleStartReading}
          >
            Start Reading
          </Button>
        </div>

        {/* Two-column secondary row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Your Journal */}
          <div className="bg-parchment dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-8 flex flex-col">
            <h2 className="font-display text-2xl font-semibold text-ink dark:text-parchment mb-3">
              Your Journal
            </h2>
            <p className="text-ink dark:text-parchment/60 font-sans mb-6 flex-grow">
              Return to your past readings, reflect on your journey, and track
              your growth. Your personal collection of insights and
              interpretations awaits.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/history')}
            >
              View Journal
            </Button>
          </div>

          {/* Explore the Guide */}
          <div className="bg-parchment/80 dark:bg-shadow/40 border border-mist dark:border-dusk rounded-xl p-8 flex flex-col">
            <h2 className="font-display text-2xl font-semibold text-ink dark:text-parchment mb-3">
              Explore the Guide
            </h2>
            <p className="text-ink dark:text-parchment/60 font-sans mb-6 flex-grow">
              Deepen your practice with our comprehensive guide. Learn about
              spread layouts, discover the rich meanings behind each card, and
              explore the ancient wisdom of tarot.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/guide')}
            >
              Open Guide
            </Button>
          </div>
        </div>

        {/* Bottom links */}
        <div className="text-center space-x-4 pt-2">
          <Link
            to="/guide/101"
            className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            Tarot 101
          </Link>
          <span className="text-mist">&bull;</span>
          <Link
            to="/about"
            className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            About Tara
          </Link>
          <span className="text-mist">&bull;</span>
          <Link
            to="/faq"
            className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            About This App
          </Link>
        </div>
      </motion.div>
    </AmbientBackground>
  );
};

export default TarotCenter;
